'use client';

import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameStats, IGame, IPlayByPlay, ISchedule } from '~/types/api';
import { GAME_STATES } from '~/utils/gameState';
import { DateTime } from 'luxon';

// Processed data interface for each game
interface GameDataEntry {
  gameId: string;
  recordStatus: string;
  gameState: string | null;
  playByPlayData: IPlayByPlay | null;
  gameStats: GameStats | null;
  lastUpdated?: Date;
  isStale?: boolean;
}

// API response interface for JSON file data
interface GameUpdateData {
  gameId: string;
  recordStatus: string;
  gameState: string | null;
  playByPlay: IPlayByPlay[] | null;
  gameStats: GameStats | null;
  lastUpdated: string;
  timestamp: number;
}

// Helper to determine if a game needs real-time updates
function needsRealtimeUpdates(game: IGame): boolean {
  return determineIfShouldTrack(game.recordStatus, game.gameDateIso);
}

// Helper to get updated game status from both individual game JSON and schedule data
async function getUpdatedGameStatus(game: IGame): Promise<{
  recordStatus: string;
  gameDateIso: string | null;
  shouldTrack: boolean;
}> {
  let recordStatus = game.recordStatus;
  let gameDateIso = game.gameDateIso;
  let gameData: any = null;
  
  // Only check individual game JSON file if the game might need real-time updates
  // This prevents hundreds of unnecessary file requests on page load
  const mightNeedUpdates = determineIfShouldTrack(game.recordStatus, game.gameDateIso);
  
  if (mightNeedUpdates) {
    // Try to get the most recent data from individual game JSON file
    try {
      gameData = await fetchGameDataFromFile(game.gameId.toString());
      if (gameData && gameData.recordStatus) {
        // console.log(`Client getUpdatedGameStatus: Individual game JSON shows status: ${gameData.recordStatus}`);
        recordStatus = gameData.recordStatus;
        // Note: Individual game files don't typically have gameDateIso, so keep the original
      }
    } catch (error) {
      // console.log(`No individual game JSON found for game ${game.gameId}`);
    }
  }
  
  // Then, try to load the schedule data for this game's season for date info
  try {
    const scheduleData = await import(`~/data/scheduleApiData_${game.seasonId}.json`);
    const schedule = scheduleData.default as ISchedule;
    
    // Find the specific game in the schedule
    const updatedGame = schedule.games?.find(g => g.gameId === game.gameId);
    
    if (updatedGame) {
      // Only use schedule recordStatus if we didn't get it from individual game JSON
      if (!gameData || !gameData.recordStatus) {
        recordStatus = updatedGame.recordStatus || recordStatus;
        // console.log(`Client getUpdatedGameStatus: Schedule data shows status: ${recordStatus}`);
      }
      gameDateIso = updatedGame.gameDateIso || gameDateIso;
    }
  } catch (error) {
    // console.log(`No schedule data found for season ${game.seasonId}, using individual game data`);
  }
  
  // Determine if we should track this game based on updated data
  const shouldTrack = determineIfShouldTrack(recordStatus, gameDateIso);
  
  // console.log(`Client getUpdatedGameStatus final: game ${game.gameId}, status: ${recordStatus}, shouldTrack: ${shouldTrack}`);
  
  return {
    recordStatus,
    gameDateIso,
    shouldTrack
  };
}

// Helper to determine if a game should be tracked based on status and date
function determineIfShouldTrack(recordStatus: string, gameDateIso: string | null): boolean {
  // Always track in-progress or delayed games
  if (recordStatus === GAME_STATES.IN_PROGRESS || recordStatus === GAME_STATES.DELAYED) {
    return true;
  }
  
  // Track recently completed games for up to 10 minutes to use JSON file data
  // until site rebuild occurs (rebuilds typically take 6-7 minutes)
  if (recordStatus === GAME_STATES.COMPLETED) {
    if (!gameDateIso) {
      return false;
    }
    
    try {
      const gameTime = DateTime.fromISO(gameDateIso);
      const now = DateTime.now();
      const minutesSinceGame = now.diff(gameTime, 'minutes').minutes;
      
      // Track completed games for 10 minutes after they finish
      // Only track games that actually finished in the past (not future-dated completed games)
      return minutesSinceGame >= 0 && minutesSinceGame <= 10;
    } catch (error) {
      // console.error('Error parsing game date:', gameDateIso, error);
      return false;
    }
  }
  
  // For other games, check if they're starting soon
  if (!gameDateIso) {
    return false;
  }
  
  try {
    const gameTime = DateTime.fromISO(gameDateIso);
    const now = DateTime.now();
    const minutesToGame = gameTime.diff(now, 'minutes').minutes;
    
    // Track games starting within 60 minutes
    return minutesToGame <= 60 && minutesToGame > -30; // Starting soon or recently finished
  } catch (error) {
    // console.error('Error parsing game date:', gameDateIso, error);
    return false;
  }
}

// Helper to register game for updates
async function registerGameForUpdates(gameId: string): Promise<void> {
  try {
    await fetch('/api/cron-game-updates', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gameId })
    });
  } catch (error) {
    // console.error(`Failed to register game ${gameId}:`, error);
  }
}

// Helper to unregister game from updates
async function unregisterGameFromUpdates(gameId: string): Promise<void> {
  try {
    await fetch('/api/cron-game-updates', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gameId })
    });
  } catch (error) {
    // console.error(`Failed to unregister game ${gameId}:`, error);
  }
}

// Cache for file existence checks to avoid repeated 404s
const fileExistenceCache = new Map<string, { exists: boolean; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds

// Helper to get WordPress CMS URL from environment variable
function getWordPressCMSUrl(): string {
  // Use the environment variable if available
  if (process.env.NEXT_PUBLIC_WORDPRESS_URL) {
    return process.env.NEXT_PUBLIC_WORDPRESS_URL;
  }
  
  // Fallback for development if environment variable is not set
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:8000';
  }
  
  // Fallback for production if environment variable is not set
  return 'https://auslcms.wpenginepowered.com';
}

// Helper to check if game data file exists via server-side API route (no CORS issues)
async function gameDataFileExists(gameId: string): Promise<boolean> {
  const cacheKey = `game-${gameId}.json`;
  const cached = fileExistenceCache.get(cacheKey);
  
  // Return cached result if it's still fresh
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.exists;
  }
  
  try {
    const apiUrl = `/api/game-data/${gameId}`;
    
    // Use HEAD request to our server-side API route - no CORS restrictions
    const response = await fetch(apiUrl, {
      method: 'HEAD',
      cache: 'no-store'
    });
    
    const exists = response.ok;
    
    // Cache the result
    fileExistenceCache.set(cacheKey, {
      exists,
      timestamp: Date.now()
    });
    
    return exists;
  } catch (error) {
    // Cache negative result
    fileExistenceCache.set(cacheKey, {
      exists: false,
      timestamp: Date.now()
    });
    return false;
  }
}

// Helper to fetch game data via server-side API route (no CORS issues)
async function fetchGameDataFromFile(gameId: string): Promise<GameUpdateData | null> {
  try {
    const apiUrl = `/api/game-data/${gameId}`;
    
    // console.log(`Fetching game data for ${gameId} via server API: ${apiUrl}`);
    
    // Fetch via our server-side API route - no CORS restrictions
    const response = await fetch(apiUrl, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        // console.log(`Game data not found for game ${gameId} (this is normal if the game is not active)`);
        return null; // File doesn't exist yet (race condition)
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    // console.log(`Successfully fetched game data for ${gameId} via server API`);
    return data;
  } catch (error) {
    // console.error(`Failed to fetch game data for ${gameId}:`, error);
    return null;
  }
}

// Define the registry return type for export
export interface GameDataRegistry {
  registerGame: (game: IGame) => Promise<void>;
  unregisterGame: (gameId: string) => void;
  activeGames: Map<string, IGame>;
}

/**
 * Global hook for managing game data polling using JSON files
 * This hook maintains a registry of active games and polls their JSON files efficiently
 */
export function useGlobalGameData(): GameDataRegistry {
  const queryClient = useQueryClient();
  const [activeGames, setActiveGames] = useState<Map<string, IGame>>(new Map());
  
  // Use a ref to store the stable registry object
  const registryRef = useRef<GameDataRegistry | null>(null);
  
  // Register a game to be tracked - use useCallback to maintain reference stability
  const registerGame = useCallback(async (game: IGame) => {
    const gameIdStr = game.gameId.toString();
    
    // Early return if already registered to prevent duplicate work
    if (activeGames.has(gameIdStr)) {
      return;
    }
    
    // Check updated status from static data before registering
    const { recordStatus, gameDateIso, shouldTrack } = await getUpdatedGameStatus(game);
    
    // Only register games that should be tracked based on updated data
    if (!shouldTrack) {
      // console.log(`Skipping registration of game ${game.gameId} - not active/upcoming (status: ${recordStatus}, date: ${gameDateIso})`);
      return;
    }
    
    setActiveGames(prev => {
      // Double-check in case of race condition
      if (prev.has(gameIdStr)) {
        return prev;
      }
      
      // console.log(`Registering game: ${game.gameId} (status: ${recordStatus}, date: ${gameDateIso})`);
      
      // Register with the server for updates (async, don't await to avoid blocking)
      registerGameForUpdates(gameIdStr);
      
      // Create an updated game object with the latest status and date
      const updatedGame: IGame = {
        ...game,
        recordStatus,
        gameDateIso
      };
      
      const newMap = new Map(prev);
      newMap.set(gameIdStr, updatedGame);
      return newMap;
    });
  }, [activeGames]);
  
  // Unregister a game when component unmounts - use useCallback for stability
  const unregisterGame = useCallback((gameId: string) => {
    setActiveGames(prev => {
      // Skip update if game isn't registered
      if (!prev.has(gameId)) {
        return prev;
      }
      
      // console.log(`Unregistering game: ${gameId}`);
      
      // Unregister from the server
      unregisterGameFromUpdates(gameId);
      
      const newMap = new Map(prev);
      newMap.delete(gameId);
      return newMap;
    });
  }, []);
  
  // Get only the games that should be actively polled
  // Since we now filter during registration, all registered games should be polled
  const gamesToPoll = Array.from(activeGames.values());
  
  // console.log(`🎮 Active games: ${activeGames.size}, Games to poll: ${gamesToPoll.length}`);
  
  // Create a stable registry object to avoid re-renders
  if (!registryRef.current) {
    registryRef.current = {
      registerGame,
      unregisterGame,
      activeGames
    };
  } else {
    // Update only the activeGames property while preserving the object reference
    registryRef.current.activeGames = activeGames;
  }
  
  return registryRef.current;
}

// Cache for polling status to ensure consistency across components
const gamePollStatusCache = new Map<string, boolean>();

/**
 * Hook to access data for a specific game using JSON file polling
 * Uses periodic fetching of static JSON files for real-time updates
 */
export function useGameItemData(game: IGame) {
  const queryClient = useQueryClient();
  const [hasReceivedUpdate, setHasReceivedUpdate] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const gameId = game.gameId.toString();
  
  // Get consistent polling status from cache or calculate once
  let shouldPoll = gamePollStatusCache.get(gameId);
  if (shouldPoll === undefined) {
    shouldPoll = needsRealtimeUpdates(game);
    gamePollStatusCache.set(gameId, shouldPoll);
    
    // Clean up cache after 5 minutes to prevent memory leaks
    setTimeout(() => {
      gamePollStatusCache.delete(gameId);
    }, 5 * 60 * 1000);
  }
  
  // Query for cached game data - use gameId as the primary key to ensure deduplication
  const { data, isLoading, error, dataUpdatedAt } = useQuery<GameDataEntry>({
    queryKey: ['game-data-cache', gameId], // Simplified key to ensure deduplication across components
    queryFn: async () => {
      // Fetch from JSON file
      // console.log(`useGameItemData: Fetching JSON file for game ${gameId}`);
      const fileData = await fetchGameDataFromFile(gameId);
      
      if (!fileData) {
        // console.log(`useGameItemData: No JSON file found for game ${gameId}, using fallback data with status: ${game.recordStatus}`);
        // Return initial data if file doesn't exist
        return {
          gameId: game.gameId.toString(),
          recordStatus: game.recordStatus,
          gameState: null,
          playByPlayData: null,
          gameStats: null,
          lastUpdated: new Date(),
          isStale: true
        };
      }
      
      // console.log(`useGameItemData: JSON file found for game ${gameId}, recordStatus: ${fileData.recordStatus}`);
      
      // Transform file data to match our interface
      return {
        gameId: fileData.gameId,
        recordStatus: fileData.recordStatus,
        gameState: fileData.gameState,
        playByPlayData: Array.isArray(fileData.playByPlay) && fileData.playByPlay.length > 0 
          ? fileData.playByPlay[0] 
          : null,
        gameStats: fileData.gameStats,
        lastUpdated: new Date(fileData.lastUpdated),
        isStale: false
      };
    },
    enabled: shouldPoll, // Only fetch if we should be polling this game
    refetchInterval: shouldPoll ? 30000 : false, // Consistent 30 second interval for all tracked games
    staleTime: 10000, // Consider data stale after 10 seconds
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    // Ensure all instances share the same query state
    notifyOnChangeProps: ['data', 'error', 'isLoading'],
    // Don't use initialData as it can override good server-side data with stale SSG data
  });

  // Set polling state for debugging
  useEffect(() => {
    setIsPolling(shouldPoll);
    // if (!shouldPoll) {
    //   console.log(`Polling disabled for game ${gameId} (not active/upcoming)`);
    // }
  }, [shouldPoll, gameId]);

  // Mark when we receive updates
  useEffect(() => {
    if (data && !data.isStale) {
      setHasReceivedUpdate(true);
    }
  }, [data]);

  return { 
    data, 
    isLoading, 
    isFetching: isPolling && shouldPoll,
    error, 
    lastUpdated: dataUpdatedAt ? new Date(dataUpdatedAt) : undefined,
    isRealtimeConnected: isPolling && shouldPoll,
    hasReceivedPollingUpdate: hasReceivedUpdate,
    needsRealtimeUpdates: shouldPoll
  };
}